import {
    AccountCategory,
    type AccountSummary,
    FRUITFUL_INSTITUTION_NAME,
    Provider,
} from '@fruitful/types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    DollarInput,
    HolisticAccountLogo,
    Icon,
    IconButton,
    Input,
    Link2Icon,
    MoreHorizontalIcon,
    PencilIcon,
    Spinner,
    SpinnerSize,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../../common/components/Toasts';
import type { MembershipDetails } from '../../../entities/entityTypes';
import { captureException } from '../../../logger/Logger';
import { GroveMoneyMapPickerField } from '../GroveMoneyMapPickerField';
import {
    isFocusMovingWithinMoneyMapTableRow,
    type MoneyMapTableRowBlurEvent,
    useMoneyMapTableRowFocusWithin,
} from '../moneyMapperTableRowFocus';
import type { OwnerPickerItem } from '../take-home-pay/moneyMapperIncomeForm';
import { INCOME_SAVED_TOAST_DEBOUNCE_MS } from '../take-home-pay/moneyMapperIncomeForm';
import {
    incomeTableRowFocusActive,
    incomeTableRowFocusIdle,
} from '../take-home-pay/moneyMapperIncomeTableLayout';
import {
    canEditLinkedAccountNicknameInTable,
    canEditLinkedHolisticCategoryInTable,
    isFruitfulHolisticAccount,
    typeColumnLabelForAccount,
} from './moneyMapperCurrentAssetsAssetTypes';
import {
    CURRENT_ASSETS_ACTIONS_OUTER_WIDTH,
    CURRENT_ASSETS_MENU_SLOT_SIZE,
    currentAssetsBalanceColumnProps,
    currentAssetsHolisticCategoryColumnProps,
    currentAssetsInstitutionColumnProps,
    currentAssetsLastFourColumnProps,
    currentAssetsLogoColumnProps,
    currentAssetsNameColumnProps,
    currentAssetsOwnershipColumnProps,
    currentAssetsTypeColumnProps,
} from './moneyMapperCurrentAssetsTableLayout';
import {
    formatAccountLastFourDisplay,
    getOwnershipShortLabel,
} from './moneyMapperCurrentAssetsUtils';
import {
    balanceDollarInputValueFromAccount,
    formFromManualAccount,
    isManualAccountBalanceInvalid,
    isManualAccountNameInvalid,
    isManualAccountRowUnchanged,
    type ManualAccountRowFormState,
    validateManualAccountRowForPersist,
} from './moneyMapperManualAccountRowForm';

/** Matches institution logos at `size="xs"` (32px) for row alignment. */
const CURRENT_ASSETS_ROW_ICON_DIAMETER = 32;

const GrayCircleAccountGlyph = ({
    children,
}: {
    children: React.ReactNode;
}) => (
    <YStack
        width={CURRENT_ASSETS_ROW_ICON_DIAMETER}
        height={CURRENT_ASSETS_ROW_ICON_DIAMETER}
        borderRadius={CURRENT_ASSETS_ROW_ICON_DIAMETER / 2}
        backgroundColor="$gray300"
        alignItems="center"
        justifyContent="center"
    >
        {children}
    </YStack>
);

/** Circular mask so bank logos match xs tile size (32px). */
const CircleClippedLogo = ({ children }: { children: React.ReactNode }) => (
    <YStack
        width={CURRENT_ASSETS_ROW_ICON_DIAMETER}
        height={CURRENT_ASSETS_ROW_ICON_DIAMETER}
        borderRadius={CURRENT_ASSETS_ROW_ICON_DIAMETER / 2}
        overflow="hidden"
        alignItems="center"
        justifyContent="center"
    >
        {children}
    </YStack>
);

const hasDisplayableInstitutionLogo = (account: AccountSummary): boolean => {
    const logo = account.logo;
    return logo != null && String(logo).trim() !== '';
};

export interface MoneyMapperCurrentAssetsAccountRowProps {
    account: AccountSummary;
    membership: MembershipDetails;
    /** ⋯ menu — uses general or accounts canEdit (same idea as take-home pay). */
    canOpenAccountMenusAndAdd: boolean;
    /** Manual-account row inputs — not gated on Fruitful holistic accounts. */
    canEditManualAccountInline: boolean;
    moneyMapSaving: boolean;
    modalBusy: boolean;
    showOwnershipColumn: boolean;
    ownerPickerItems: OwnerPickerItem[];
    holisticCategoryPickerItems: Array<{
        label: string;
        value: AccountCategory;
    }>;
    onOpenAccountMenu: (account: AccountSummary) => void;
    onPersistManualAccount: (
        account: AccountSummary,
        form: ManualAccountRowFormState,
    ) => Promise<void>;
    onPersistLinkedHolisticCategory: (
        account: AccountSummary,
        holisticCategory: AccountCategory,
    ) => Promise<void>;
    onPersistLinkedAccountNickname: (
        account: AccountSummary,
        nextName: string,
    ) => Promise<void>;
}

const CurrentAssetsAccountIcon = ({ account }: { account: AccountSummary }) => {
    const isManual = account.provider === Provider.MANUAL;

    if (isManual) {
        return (
            <YStack {...currentAssetsLogoColumnProps} justifyContent="center">
                <GrayCircleAccountGlyph>
                    <Icon icon={PencilIcon} size={16} color="$gray1100" />
                </GrayCircleAccountGlyph>
            </YStack>
        );
    }

    if (isFruitfulHolisticAccount(account)) {
        return (
            <YStack {...currentAssetsLogoColumnProps}>
                <CircleClippedLogo>
                    <HolisticAccountLogo
                        institutionName={FRUITFUL_INSTITUTION_NAME}
                        logo={account.logo}
                        provider={account.provider}
                        size="xs"
                        showShadow={false}
                    />
                </CircleClippedLogo>
            </YStack>
        );
    }

    if (hasDisplayableInstitutionLogo(account)) {
        return (
            <YStack {...currentAssetsLogoColumnProps}>
                <CircleClippedLogo>
                    <HolisticAccountLogo
                        institutionName={account.institutionName}
                        logo={account.logo}
                        size="xs"
                        showShadow={false}
                    />
                </CircleClippedLogo>
            </YStack>
        );
    }

    return (
        <YStack {...currentAssetsLogoColumnProps} justifyContent="center">
            <GrayCircleAccountGlyph>
                <Icon icon={Link2Icon} size={16} color="$gray1100" />
            </GrayCircleAccountGlyph>
        </YStack>
    );
};

export const MoneyMapperCurrentAssetsAccountRow = ({
    account,
    membership,
    canOpenAccountMenusAndAdd,
    canEditManualAccountInline,
    moneyMapSaving,
    modalBusy,
    showOwnershipColumn,
    ownerPickerItems,
    holisticCategoryPickerItems,
    onOpenAccountMenu,
    onPersistManualAccount,
    onPersistLinkedHolisticCategory,
    onPersistLinkedAccountNickname,
}: MoneyMapperCurrentAssetsAccountRowProps) => {
    const toast = useToastController();
    const isManual = account.provider === Provider.MANUAL;

    const [draft, setDraft] = useState<ManualAccountRowFormState>(() =>
        formFromManualAccount(account),
    );
    const [linkedHolisticDraft, setLinkedHolisticDraft] = useState(
        () => account.category ?? AccountCategory.SAVE,
    );
    const [linkedNameDraft, setLinkedNameDraft] = useState(
        () => account.name ?? '',
    );
    const [showNameInvalid, setShowNameInvalid] = useState(false);
    const [showBalanceInvalid, setShowBalanceInvalid] = useState(false);
    const [showLinkedNameInvalid, setShowLinkedNameInvalid] = useState(false);
    const [isRowSaving, setIsRowSaving] = useState(false);
    const draftRef = useRef(draft);
    draftRef.current = draft;
    const linkedNameDraftRef = useRef(linkedNameDraft);
    linkedNameDraftRef.current = linkedNameDraft;
    const lastSavedToastAtRef = useRef(0);
    const tableRowRef = useRef<HTMLElement | null>(null);
    const [tableRowRootElement, setTableRowRootElement] =
        useState<HTMLElement | null>(null);
    const assignTableRowRootRef = useCallback((node: HTMLElement | null) => {
        tableRowRef.current = node;
        setTableRowRootElement(node);
    }, []);
    const hasFocusWithinTableRow =
        useMoneyMapTableRowFocusWithin(tableRowRootElement);

    useEffect(() => {
        setDraft(formFromManualAccount(account));
        setShowNameInvalid(false);
        setShowBalanceInvalid(false);
    }, [
        account.id,
        account.name,
        account.userId,
        account.category,
        account.currentBalanceCents,
        account.institutionName,
    ]);

    useEffect(() => {
        setLinkedHolisticDraft(account.category ?? AccountCategory.SAVE);
    }, [account.id, account.category]);

    useEffect(() => {
        if (!isManual) {
            setLinkedNameDraft(account.name ?? '');
        }
    }, [isManual, account.id, account.name]);

    useEffect(() => {
        setShowLinkedNameInvalid(false);
    }, [account.id, account.name]);

    const manualInputsDisabled =
        !canEditManualAccountInline ||
        moneyMapSaving ||
        modalBusy ||
        isRowSaving ||
        (isManual && ownerPickerItems.length === 0);

    /** Manual rows only — linked controls use flags aligned with AccountEditModal + canOpenAccountMenusAndAdd. */
    const disabled = manualInputsDisabled;

    const tryPersistManual = async (next: ManualAccountRowFormState) => {
        if (
            !canEditManualAccountInline ||
            moneyMapSaving ||
            modalBusy ||
            isRowSaving
        ) {
            return;
        }
        if (!validateManualAccountRowForPersist(next).ok) {
            return;
        }
        if (isManualAccountRowUnchanged(account, next)) {
            return;
        }

        setIsRowSaving(true);
        try {
            await onPersistManualAccount(account, next);
            const now = Date.now();
            if (
                now - lastSavedToastAtRef.current >=
                INCOME_SAVED_TOAST_DEBOUNCE_MS
            ) {
                lastSavedToastAtRef.current = now;
                toast.show('Account saved', MutationSuccessToastOptions);
            }
        } catch (error) {
            captureException(error);
            toast.show(
                'Error saving account',
                MutationErrorToastOptions(error),
            );
        } finally {
            setIsRowSaving(false);
        }
    };

    const tryPersistLinkedHolistic = async (next: AccountCategory) => {
        if (
            !canOpenAccountMenusAndAdd ||
            moneyMapSaving ||
            modalBusy ||
            isRowSaving ||
            !canEditLinkedHolisticCategoryInTable(account)
        ) {
            return;
        }
        if (next === account.category) {
            return;
        }

        setIsRowSaving(true);
        try {
            await onPersistLinkedHolisticCategory(account, next);
            const now = Date.now();
            if (
                now - lastSavedToastAtRef.current >=
                INCOME_SAVED_TOAST_DEBOUNCE_MS
            ) {
                lastSavedToastAtRef.current = now;
                toast.show('Account saved', MutationSuccessToastOptions);
            }
        } catch (error) {
            captureException(error);
            toast.show(
                'Error saving account',
                MutationErrorToastOptions(error),
            );
        } finally {
            setIsRowSaving(false);
        }
    };

    const tryPersistLinkedNickname = async () => {
        const canEditNickname =
            canOpenAccountMenusAndAdd &&
            canEditLinkedAccountNicknameInTable(account);
        if (!canEditNickname || moneyMapSaving || modalBusy || isRowSaving) {
            return;
        }
        const trimmed = linkedNameDraft.trim();
        if (trimmed === '') {
            return;
        }
        if (trimmed === (account.name ?? '').trim()) {
            return;
        }

        setIsRowSaving(true);
        try {
            await onPersistLinkedAccountNickname(account, linkedNameDraft);
            const now = Date.now();
            if (
                now - lastSavedToastAtRef.current >=
                INCOME_SAVED_TOAST_DEBOUNCE_MS
            ) {
                lastSavedToastAtRef.current = now;
                toast.show('Account saved', MutationSuccessToastOptions);
            }
        } catch (error) {
            captureException(error);
            toast.show(
                'Error saving account',
                MutationErrorToastOptions(error),
            );
        } finally {
            setIsRowSaving(false);
        }
    };

    const persistManualIfLeavingRow = (event: MoneyMapTableRowBlurEvent) => {
        const root = tableRowRef.current as unknown as
            | Element
            | null
            | undefined;
        if (isFocusMovingWithinMoneyMapTableRow(root, event)) {
            return;
        }
        void tryPersistManual(draftRef.current);
    };

    const persistLinkedNicknameIfLeavingRow = (
        event: MoneyMapTableRowBlurEvent,
    ) => {
        const root = tableRowRef.current as unknown as
            | Element
            | null
            | undefined;
        if (isFocusMovingWithinMoneyMapTableRow(root, event)) {
            return;
        }
        void tryPersistLinkedNickname();
    };

    const lastFour = formatAccountLastFourDisplay(account.accountNumberMasked);
    const ownershipLabel = getOwnershipShortLabel(membership, account);
    const linkedNicknameEditable =
        canOpenAccountMenusAndAdd &&
        canEditLinkedAccountNicknameInTable(account);

    const linkedRowBusy = moneyMapSaving || modalBusy || isRowSaving;
    const linkedNameInputDisabled = !linkedNicknameEditable || linkedRowBusy;

    const linkedHolisticPickerDisabled =
        linkedRowBusy ||
        !canOpenAccountMenusAndAdd ||
        !canEditLinkedHolisticCategoryInTable(account);

    const menuDisabled =
        !canOpenAccountMenusAndAdd || moneyMapSaving || modalBusy;

    /** Grey rules + fill while any control in the row is focused (same tokens as take-home pay). */
    const tableRowEditingSurfaceProps = hasFocusWithinTableRow
        ? incomeTableRowFocusActive
        : incomeTableRowFocusIdle;

    const focusStaysInsideTableRow = (event: MoneyMapTableRowBlurEvent) =>
        isFocusMovingWithinMoneyMapTableRow(
            tableRowRef.current as unknown as Element | null,
            event,
        );

    if (!isManual) {
        return (
            <XStack
                ref={assignTableRowRootRef}
                width="100%"
                alignItems="flex-start"
                paddingVertical={12}
                paddingHorizontal={4}
                gap={8}
                {...tableRowEditingSurfaceProps}
            >
                <YStack {...currentAssetsNameColumnProps}>
                    <Input
                        value={linkedNameDraft}
                        invalid={showLinkedNameInvalid}
                        onChangeText={(t) => {
                            setLinkedNameDraft(t);
                            if (t.trim().length > 0) {
                                setShowLinkedNameInvalid(false);
                            }
                        }}
                        onBlur={(e) => {
                            setShowLinkedNameInvalid(
                                linkedNameDraftRef.current.trim() === '',
                            );
                            if (!focusStaysInsideTableRow(e)) {
                                void tryPersistLinkedNickname();
                            }
                        }}
                        placeholder="Account name"
                        disabled={linkedNameInputDisabled}
                    />
                </YStack>
                <YStack {...currentAssetsInstitutionColumnProps}>
                    <Input value={account.institutionName ?? ''} disabled />
                </YStack>
                {showOwnershipColumn && (
                    <YStack {...currentAssetsOwnershipColumnProps}>
                        <Input value={ownershipLabel} disabled />
                    </YStack>
                )}
                <YStack {...currentAssetsTypeColumnProps}>
                    <Input
                        value={typeColumnLabelForAccount(account)}
                        disabled
                    />
                </YStack>
                <YStack {...currentAssetsHolisticCategoryColumnProps}>
                    <GroveMoneyMapPickerField
                        size="regular"
                        value={linkedHolisticDraft}
                        options={holisticCategoryPickerItems}
                        disabled={linkedHolisticPickerDisabled}
                        onTriggerBlur={persistLinkedNicknameIfLeavingRow}
                        onChange={(v) => {
                            setLinkedHolisticDraft(v);
                            void tryPersistLinkedHolistic(v);
                        }}
                    />
                </YStack>
                <YStack {...currentAssetsLastFourColumnProps}>
                    <Input value={lastFour} disabled />
                </YStack>
                <YStack {...currentAssetsBalanceColumnProps}>
                    <DollarInput
                        value={balanceDollarInputValueFromAccount(account)}
                        onChangeText={() => {}}
                        allowDecimals={false}
                        disabled
                    />
                </YStack>
                <XStack
                    width={CURRENT_ASSETS_ACTIONS_OUTER_WIDTH}
                    flexShrink={0}
                    minHeight={44}
                    alignItems="flex-start"
                    justifyContent="flex-end"
                    gap={4}
                    paddingTop={4}
                >
                    <CurrentAssetsAccountIcon account={account} />
                    <YStack
                        width={CURRENT_ASSETS_MENU_SLOT_SIZE}
                        height={CURRENT_ASSETS_MENU_SLOT_SIZE}
                        flexShrink={0}
                        alignItems="center"
                        justifyContent="center"
                    >
                        {isRowSaving ? (
                            <Spinner size={SpinnerSize.SMALL} />
                        ) : (
                            <IconButton
                                variant="tertiary"
                                icon={MoreHorizontalIcon}
                                disabled={menuDisabled}
                                onPress={() => {
                                    onOpenAccountMenu(account);
                                }}
                            />
                        )}
                    </YStack>
                </XStack>
            </XStack>
        );
    }

    return (
        <XStack
            ref={assignTableRowRootRef}
            width="100%"
            alignItems="flex-start"
            paddingVertical={12}
            paddingHorizontal={4}
            gap={8}
            {...tableRowEditingSurfaceProps}
        >
            <YStack {...currentAssetsNameColumnProps}>
                <Input
                    value={draft.name}
                    invalid={showNameInvalid}
                    onChangeText={(t) => {
                        setDraft((d) => ({ ...d, name: t }));
                        if (t.trim().length > 0) {
                            setShowNameInvalid(false);
                        }
                    }}
                    onBlur={(e) => {
                        setShowNameInvalid(
                            isManualAccountNameInvalid(draftRef.current.name),
                        );
                        if (!focusStaysInsideTableRow(e)) {
                            void tryPersistManual(draftRef.current);
                        }
                    }}
                    placeholder="Account name"
                    disabled={disabled}
                />
            </YStack>
            <YStack {...currentAssetsInstitutionColumnProps}>
                <Input value={account.institutionName ?? ''} disabled />
            </YStack>
            {showOwnershipColumn && (
                <YStack {...currentAssetsOwnershipColumnProps}>
                    <GroveMoneyMapPickerField
                        size="regular"
                        value={draft.userId}
                        options={ownerPickerItems}
                        disabled={disabled}
                        onTriggerBlur={persistManualIfLeavingRow}
                        onChange={(v) => {
                            const next = { ...draft, userId: v };
                            setDraft(next);
                            void tryPersistManual(next);
                        }}
                    />
                </YStack>
            )}
            <YStack {...currentAssetsTypeColumnProps}>
                <Input value={typeColumnLabelForAccount(account)} disabled />
            </YStack>
            <YStack {...currentAssetsHolisticCategoryColumnProps}>
                <GroveMoneyMapPickerField
                    size="regular"
                    value={draft.holisticCategory}
                    options={holisticCategoryPickerItems}
                    disabled={disabled}
                    onTriggerBlur={persistManualIfLeavingRow}
                    onChange={(v) => {
                        const next = { ...draft, holisticCategory: v };
                        setDraft(next);
                        void tryPersistManual(next);
                    }}
                />
            </YStack>
            <YStack {...currentAssetsLastFourColumnProps}>
                <Input value={lastFour} disabled />
            </YStack>
            <YStack {...currentAssetsBalanceColumnProps}>
                <DollarInput
                    value={draft.balanceStr}
                    invalid={showBalanceInvalid}
                    onChangeText={(t) => {
                        setDraft((d) => ({ ...d, balanceStr: t }));
                        if (!isManualAccountBalanceInvalid(t)) {
                            setShowBalanceInvalid(false);
                        }
                    }}
                    onBlur={(e) => {
                        setShowBalanceInvalid(
                            isManualAccountBalanceInvalid(
                                draftRef.current.balanceStr,
                            ),
                        );
                        if (!focusStaysInsideTableRow(e)) {
                            void tryPersistManual(draftRef.current);
                        }
                    }}
                    allowDecimals={false}
                    disabled={disabled}
                />
            </YStack>
            <XStack
                width={CURRENT_ASSETS_ACTIONS_OUTER_WIDTH}
                flexShrink={0}
                minHeight={44}
                alignItems="flex-start"
                justifyContent="flex-end"
                gap={4}
                paddingTop={4}
            >
                <CurrentAssetsAccountIcon account={account} />
                <YStack
                    width={CURRENT_ASSETS_MENU_SLOT_SIZE}
                    height={CURRENT_ASSETS_MENU_SLOT_SIZE}
                    flexShrink={0}
                    alignItems="center"
                    justifyContent="center"
                >
                    {isRowSaving ? (
                        <Spinner size={SpinnerSize.SMALL} />
                    ) : (
                        <IconButton
                            variant="tertiary"
                            icon={MoreHorizontalIcon}
                            disabled={menuDisabled}
                            onPress={() => {
                                onOpenAccountMenu(account);
                            }}
                        />
                    )}
                </YStack>
            </XStack>
        </XStack>
    );
};
